import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { TeamLogo } from "../shared/TeamLogo";
import { nflTeamObject } from "../../constants/nflTeams";

const STATE_BADGE = {
  normal: { label: "Model Projection", className: "border-eb-accent/40 bg-eb-accent/10 text-eb-accent" },
  qb_scenario: { label: "Conditional Scenarios", className: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400" },
  withheld: { label: "Projection Withheld", className: "border-red-500/40 bg-red-500/10 text-red-400" },
  final: { label: "Final", className: "border-white/20 bg-white/5 text-zinc-300" },
  result_unavailable: { label: "Result Pending Sync", className: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400" },
};

const pct0 = (x) => `${(x * 100).toFixed(0)}%`;
const pct1 = (x) => `${(x * 100).toFixed(1)}%`;

// One clearly-labeled market per compact row -- the model's own probability (primary, always
// present) on the left, the matched sportsbook's actual price (secondary, book-attributed) on
// the right. Never blended into a single number, so neither one is ever mistaken for the other.
const MarketRow = ({ testId, label, modelText, bookText }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5 font-mono text-[11px] text-zinc-300 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
    <div className="flex items-baseline gap-2 sm:contents">
      <span className="w-[72px] shrink-0 uppercase tracking-wide text-zinc-500">{label}</span>
      <span className="sm:flex-1">{modelText}</span>
    </div>
    <span className="pl-[72px] text-zinc-600 sm:pl-0 sm:shrink-0 sm:text-right sm:text-zinc-500">{bookText}</span>
  </div>
);

const ScenarioRow = ({ s, testId }) => (
  <div data-testid={testId} className="flex items-center justify-between gap-3 rounded border border-white/5 bg-black/20 px-2.5 py-1.5 font-mono text-[11px]">
    <span className={s.usable ? "text-zinc-300" : "text-zinc-600"}>{s.label}</span>
    {s.usable ? (
      <span className="text-zinc-400">
        {s.home_points} - {s.away_points} &middot; margin {s.home_margin > 0 ? "+" : ""}
        {s.home_margin} &middot; total {s.total} <span className="text-zinc-600">({s.n_games_used}g)</span>
      </span>
    ) : (
      <span className="text-zinc-600">no eligible cached games &mdash; not shown</span>
    )}
  </div>
);

const AvailabilityDetail = ({ team, a, testId }) => {
  const reportPublished = a.target_week_injury_report?.any_report_published_for_this_team_this_week;
  const odq = a.team_out_doubtful_questionable_target_week || [];
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5 font-mono text-[10px] text-zinc-500">
      <span>
        <span className="text-zinc-400">{team}</span> QB (model): {a.depth_chart_listed_qb?.player_name || "unresolved"}
      </span>
      <span>
        {reportPublished ? (
          odq.length > 0 ? `Out/Doubtful/Questionable: ${odq.map((r) => `${r.player_name} (${r.position}, ${r.status})`).join(", ")}` : "No Out/Doubtful/Questionable this week"
        ) : (
          <span className="text-zinc-600">Availability unknown &mdash; no week-{a.target_week_injury_report?.week} injury report published yet</span>
        )}
      </span>
    </div>
  );
};

// Compact card mirroring GameCard.jsx's baseball layout: logos/names + kickoff on the left,
// status/score/projection in the middle, state badge on the right -- then clearly-labeled
// Moneyline/Spread/Total rows, any warnings that must stay visible, and a collapsed "Model
// details" section for the technical probability breakdown + full availability context.
export const NflPreviewCard = ({ game }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const badge = STATE_BADGE[game.state] || STATE_BADGE.withheld;
  const awayTeam = nflTeamObject(game.away_team);
  const homeTeam = nflTeamObject(game.home_team);
  const kickoff = `${game.weekday}, ${game.gameday} \u00b7 ${game.gametime_et} ET`;

  const ml = game.probabilities?.moneyline;
  const spread = game.probabilities?.spread;
  const total = game.probabilities?.total;
  const odds = game.odds;

  // Never hidden behind "Model details": QB identity mismatch vs. depth chart, and any real
  // Out/Doubtful/Questionable name for either team this week.
  const availabilityEntries = game.availability ? Object.entries(game.availability) : [];
  const mismatchTeams = availabilityEntries.filter(([, a]) => a.qb_identity_mismatch === true).map(([team]) => team);
  const odqByTeam = availabilityEntries.map(([team, a]) => ({ team, list: a.team_out_doubtful_questionable_target_week || [] })).filter((t) => t.list.length > 0);

  return (
    <div data-testid={`nfl-preview-card-${game.game_id}`} className="flex flex-col gap-3 rounded-lg border border-white/10 bg-eb-surface p-4 transition-colors hover:border-white/20">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-1.5 font-heading text-base text-white">
            <TeamLogo team={awayTeam} size={24} ringColor={awayTeam.color} testId={`nfl-logo-away-${game.game_id}`} />
            <span>{awayTeam.name}</span>
            <span className="text-xs text-zinc-500">@</span>
            <TeamLogo team={homeTeam} size={24} ringColor={homeTeam.color} testId={`nfl-logo-home-${game.game_id}`} />
            <span>{homeTeam.name}</span>
          </div>
          <span className="font-mono text-xs text-zinc-500">
            {kickoff} &middot; {game.venue.stadium}
            {game.venue.location === "Neutral" && (
              <span data-testid={`nfl-neutral-badge-${game.game_id}`} className="ml-1.5 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-zinc-300">
                Neutral Site
              </span>
            )}
          </span>
        </div>

        <span data-testid={`nfl-state-badge-${game.game_id}`} className={`shrink-0 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      {/* Score/status block -- sits directly under the team names, final score and the
          model's own FROZEN pregame projection (never recomputed after the fact) shown
          together so the two can be compared at a glance. */}
      <div data-testid={`nfl-status-${game.game_id}`} className="rounded-md bg-black/20 px-3 py-2 font-mono text-xs">
        {game.state === "final" && game.final_score ? (
          <div data-testid={`nfl-final-score-${game.game_id}`} className="flex flex-col gap-0.5">
            <span className="text-sm text-zinc-200">
              Final &middot; {awayTeam.abbreviation} {game.final_score.away} &ndash; {homeTeam.abbreviation} {game.final_score.home}
              {game.overtime && <span className="ml-1 text-[10px] uppercase text-zinc-500">OT</span>}
            </span>
            {game.pregame_projection ? (
              <span data-testid={`nfl-pregame-projection-${game.game_id}`} className="text-zinc-500" title={game.pregame_projection.margin_label}>
                Model had: {awayTeam.abbreviation} {game.pregame_projection.away_points} &ndash; {homeTeam.abbreviation} {game.pregame_projection.home_points} &middot; total{" "}
                {game.pregame_projection.total}
              </span>
            ) : (
              <span className="text-zinc-700">No frozen pregame projection on record for this game</span>
            )}
          </div>
        ) : game.state === "result_unavailable" ? (
          <span className="text-yellow-400">Result pending sync</span>
        ) : game.projection ? (
          <span className="text-zinc-400" title={game.projection.margin_label}>
            Model: {awayTeam.abbreviation} {game.projection.away_points} &ndash; {homeTeam.abbreviation} {game.projection.home_points} &middot; margin{" "}
            {game.projection.home_margin > 0 ? "+" : ""}
            {game.projection.home_margin} &middot; total {game.projection.total}
          </span>
        ) : (
          <span className="text-zinc-600">No projection available</span>
        )}
      </div>

      {game.state === "normal" && ml && (
        <div data-testid={`nfl-markets-${game.game_id}`} className="flex flex-col gap-1.5 rounded-md border border-eb-accent/20 bg-eb-accent/5 p-3">
          <MarketRow
            testId={`nfl-ml-row-${game.game_id}`}
            label="Moneyline"
            modelText={`${awayTeam.abbreviation} ${pct0(ml.p_away_win)} / ${homeTeam.abbreviation} ${pct0(ml.p_home_win)}`}
            bookText={odds?.matched ? `${odds.sportsbook}${odds.stale ? " (stale)" : ""}` : "no book line"}
          />
          {spread && odds?.spread && (
            <MarketRow

              testId={`nfl-spread-row-${game.game_id}`}
              label="Spread"
              modelText={`${homeTeam.abbreviation} ${spread.line > 0 ? "+" : ""}${spread.line} \u00b7 cover ${pct0(spread.p_home_cover)}`}
              bookText={`${odds.spread.home_price} / ${odds.spread.away_price}`}
            />
          )}
          {total && odds?.total && (
            <MarketRow
              testId={`nfl-total-row-${game.game_id}`}
              label="Total"
              modelText={`${total.line} \u00b7 O ${pct0(total.p_over)} / U ${pct0(total.p_under)}`}
              bookText={`O ${odds.total.over_price} / U ${odds.total.under_price}`}
            />
          )}
          {!odds?.matched && (
            <p data-testid={`nfl-odds-unmatched-${game.game_id}`} className="font-mono text-[10px] text-zinc-600">
              No bookmaker odds matched for this game yet &mdash; moneyline above uses the model's own margin distribution only (line=0).
            </p>
          )}
        </div>
      )}

      {game.state === "qb_scenario" && game.qb_scenarios && (
        <div data-testid={`nfl-qb-scenarios-${game.game_id}`} className="flex flex-col gap-2">
          <p className="font-mono text-[11px] text-yellow-400">
            No single default projection &mdash; the auto-selected QB no longer matches the depth chart. Experimental conditional scenarios shown instead:
          </p>
          {Object.entries(game.qb_scenarios).map(([team, scenarios]) => (
            <div key={team} className="flex flex-col gap-1">
              <span className="font-mono text-[10px] uppercase text-zinc-500">{team}</span>
              {scenarios.map((s, i) => (
                <ScenarioRow key={s.player_id} s={s} testId={`nfl-scenario-${game.game_id}-${team}-${i}`} />
              ))}
            </div>
          ))}
        </div>
      )}

      {game.state === "withheld" && game.withheld_reasons && (
        <div data-testid={`nfl-withheld-reasons-${game.game_id}`} className="flex flex-col gap-1.5 rounded-md border border-red-500/20 bg-red-500/5 p-3">
          {game.withheld_reasons.map((r, i) => (
            <p key={i} className="font-mono text-[11px] leading-relaxed text-red-300">
              {r}
            </p>
          ))}
        </div>
      )}

      {game.state === "result_unavailable" && (
        <div data-testid={`nfl-result-unavailable-${game.game_id}`} className="rounded-md border border-yellow-500/20 bg-yellow-500/5 p-3 font-mono text-[11px] leading-relaxed text-yellow-300">
          {game.result_unavailable_reason || "Result not yet available \u2014 kickoff has passed but the score hasn't synced yet."}
        </div>
      )}

      {mismatchTeams.length > 0 && (
        <p data-testid={`nfl-qb-mismatch-${game.game_id}`} className="font-mono text-[11px] text-yellow-400">
          QB mismatch vs. depth chart: {mismatchTeams.join(", ")}
        </p>
      )}

      {odqByTeam.length > 0 && (
        <div data-testid={`nfl-odq-${game.game_id}`} className="flex flex-col gap-1 rounded-md border border-yellow-500/20 bg-yellow-500/5 p-3 font-mono text-[11px] text-yellow-300">
          {odqByTeam.map((t) => (
            <span key={t.team}>
              {t.team}: {t.list.map((r) => `${r.player_name} (${r.position}, ${r.status})`).join(", ")}
            </span>
          ))}
        </div>
      )}

      {(availabilityEntries.length > 0 || ml) && (
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <CollapsibleTrigger
            data-testid={`nfl-details-toggle-${game.game_id}`}
            className="flex items-center gap-1.5 self-start font-mono text-[10px] uppercase tracking-wide text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Model details <CaretDown size={10} className={`transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent data-testid={`nfl-details-content-${game.game_id}`} className="flex flex-col gap-2 border-t border-white/5 pt-2">
            {ml && (
              <p
                className="font-mono text-[10px] text-zinc-600"
                title="P(win) already accounts for the tie chance. 'If no tie' instead asks: given the game is NOT a tie, who wins?"
              >
                Tie (unconditional): {pct1(ml.p_tie)} &middot; If no tie: {homeTeam.abbreviation} {pct1(ml.p_home_win_given_no_tie)} / {awayTeam.abbreviation}{" "}
                {pct1(ml.p_away_win_given_no_tie)}
              </p>
            )}
            {spread && <p className="font-mono text-[10px] text-zinc-600">Spread fail/push: {pct1(spread.p_home_fail)} / {pct1(spread.p_push)}</p>}
            {total && <p className="font-mono text-[10px] text-zinc-600">Total push: {pct1(total.p_push)}</p>}
            {availabilityEntries.map(([team, a]) => (
              <AvailabilityDetail key={team} team={team} a={a} testId={`nfl-availability-${team}-${game.game_id}`} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
